import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { LimitsBannerM2M3 } from '@jira/platform__banners-limits-banner/src/LimitsBannerLimitsBannerV2.tsx';
import { defineMessage } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { WorkflowsLimitsBannerQuery } from './__generated__/WorkflowsLimitsBannerQuery.graphql.ts';

// Backend entity key for the workflows-per-scheme limit exposed via jira.limitValues.
const WORKFLOWS_ENTITY_KEY = 'workflows.per.workflowscheme.limit';

// Fallback used only when the backend does not return a workflows-per-scheme limit.
const DEFAULT_WORKFLOWS_LIMIT = 150;

const LIMITS_GUARDRAILS_LINK =
	'https://support.atlassian.com/jira-cloud-administration/docs/data-limits-and-guardrails/';

const preEnforcementLimitsBannerMessage = defineMessage({
	id: 'router-routes-legacy-project-settings-workflows-entries.pre-enforcement-limits-banner-message',
	defaultMessage:
		'Starting in September, workflow schemes will be limited to {limit} workflows. Review usage and <link>Learn More</link> about your limits.',
	description:
		'Warning banner message shown on the project settings workflows page to notify admins of the upcoming workflow scheme limit enforcement.',
});

export const WorkflowsLimitsBanner = (): React.JSX.Element => {
	const cloudId = useCloudId();
	const data = useLazyLoadQuery<WorkflowsLimitsBannerQuery>(
		graphql`
			query WorkflowsLimitsBannerQuery($cloudId: ID!, $filterKeys: [String!]!) {
				jira @optIn(to: "JiraResourceUsageMetricLimitValue") {
					limitValues(cloudId: $cloudId, filterKeys: $filterKeys) {
						key
						limitValue
					}
				}
			}
		`,
		{ cloudId, filterKeys: [WORKFLOWS_ENTITY_KEY] },
	);

	// Use the workflows-per-scheme limit provided by the backend, falling back to the
	// default when the backend does not expose a value or returns a non-positive one.
	const backendWorkflowsLimit = data.jira?.limitValues?.find(
		(entry) => entry?.key === WORKFLOWS_ENTITY_KEY,
	)?.limitValue;
	const workflowsLimit =
		backendWorkflowsLimit != null && backendWorkflowsLimit > 0
			? backendWorkflowsLimit
			: DEFAULT_WORKFLOWS_LIMIT;

	return (
		<Box xcss={fullBleedBannerWrapper}>
			<LimitsBannerM2M3
				message={preEnforcementLimitsBannerMessage}
				limit={workflowsLimit}
				link={LIMITS_GUARDRAILS_LINK}
			/>
		</Box>
	);
};

const fullBleedBannerWrapper = xcss({
	marginInline: 'space.negative.300',
	marginBottom: 'space.150',
});
